import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

/**
 * Global confirmation / prompt provider.
 *
 * Replaces the browser-native `window.confirm()` and `window.prompt()` —
 * which render a white blocking banner pegado al header that breaks the
 * Commandix visual language — with a centered Shadcn Dialog that matches
 * the rest of the app.
 *
 * Usage anywhere in the tree:
 *   const { confirm, prompt } = useConfirm();
 *   const ok = await confirm({ title, message, confirmText, variant: "destructive" });
 *   const text = await prompt({ title, message, placeholder });
 */
const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("confirm"); // "confirm" | "prompt"
  const [opts, setOpts] = useState({});
  const [value, setValue] = useState("");
  const resolveRef = useRef(null);

  const close = (result) => {
    setOpen(false);
    if (resolveRef.current) {
      const r = resolveRef.current;
      resolveRef.current = null;
      r(result);
    }
  };

  const confirm = useCallback((o = {}) => new Promise((resolve) => {
    setMode("confirm");
    setOpts(o);
    setValue("");
    resolveRef.current = resolve;
    setOpen(true);
  }), []);

  const prompt = useCallback((o = {}) => new Promise((resolve) => {
    setMode("prompt");
    setOpts(o);
    setValue(o.defaultValue || "");
    resolveRef.current = resolve;
    setOpen(true);
  }), []);

  const variant = opts.variant || "primary";
  const confirmBtnClass =
    variant === "destructive"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : variant === "warning"
      ? "bg-amber-600 hover:bg-amber-700 text-white"
      : variant === "success"
      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
      : "bg-primary hover:bg-primary/90";

  return (
    <ConfirmCtx.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(mode === "prompt" ? null : false); }}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {opts.title || (mode === "prompt" ? "Ingresa un valor" : "Confirmar acción")}
            </DialogTitle>
            {opts.message && (
              <DialogDescription className="whitespace-pre-line text-foreground/80 text-sm pt-1">
                {opts.message}
              </DialogDescription>
            )}
          </DialogHeader>

          {mode === "prompt" && (
            opts.multiline ? (
              <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={opts.placeholder || ""}
                rows={4}
                className="bg-secondary border-border mt-2"
                autoFocus
                data-testid="confirm-prompt-input"
              />
            ) : (
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={opts.placeholder || ""}
                className="bg-secondary border-border mt-2"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") close(value);
                }}
                data-testid="confirm-prompt-input"
              />
            )
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => close(mode === "prompt" ? null : false)}
              data-testid="confirm-cancel"
            >
              {opts.cancelText || "Cancelar"}
            </Button>
            <Button
              onClick={() => close(mode === "prompt" ? value : true)}
              className={confirmBtnClass}
              data-testid="confirm-ok"
            >
              {opts.confirmText || (mode === "prompt" ? "Aceptar" : "Confirmar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
